// src/Components/Modules/HR/EmployeeDashboard.js
import React, { useEffect, useState, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Grid, Card, Button, Chip, IconButton,
  Tooltip, Skeleton, Stack, Avatar, Badge as MuiBadge, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Radio, FormControlLabel, Tabs, Tab, CircularProgress,
} from "@mui/material";
import {
  FaClock, FaCalendarAlt, FaMoneyBillWave, FaFileAlt, FaCalendarCheck,
  FaBell, FaClipboardList, FaRobot, FaArrowRight, FaSync, FaDownload,
  FaCheckCircle, FaFileInvoiceDollar, FaFingerprint, FaIdCard, FaBoxOpen,
  FaSignOutAlt, FaMapMarkerAlt, FaGlobe, FaTimesCircle, FaHourglassHalf,
  FaPaperPlane, FaHandHoldingUsd, FaPlus, FaTimes, FaChartLine,
} from "react-icons/fa";
import { styled, keyframes } from "@mui/material/styles";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis,
  Tooltip as RechartsTooltip, RadialBarChart, RadialBar,
} from "recharts";
import Swal from "sweetalert2";

import NotificationCenter from "./NotificationCenter";

// ============== Animations ==============
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;
const pulse = keyframes`0%, 100% { opacity: 1; } 50% { opacity: 0.4; }`;

// ============== Styled Components ==============
const HeroCard = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(99, 102, 241, 0.25)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50%",
    right: "-10%",
    width: "500px",
    height: "500px",
    background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
    borderRadius: "50%",
  },
}));

const PunchHero = styled(Paper)(({ theme, status }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  color: "white",
  background:
    status === "in"
      ? "linear-gradient(135deg, #059669 0%, #10b981 100%)"
      : status === "out"
      ? "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)"
      : "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
  boxShadow:
    status === "in"
      ? "0 10px 40px rgba(16, 185, 129, 0.3)"
      : status === "out"
      ? "0 10px 40px rgba(239, 68, 68, 0.3)"
      : "0 10px 40px rgba(99, 102, 241, 0.25)",
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  border: "1px solid #e2e8f0",
  backgroundColor: "white",
  transition: "all 0.2s ease",
  height: "100%",
  "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 8px 20px rgba(0,0,0,0.06)", transform: "translateY(-2px)" },
}));

const QuickActionCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  border: "1px solid #e2e8f0",
  cursor: "pointer",
  transition: "all 0.25s ease",
  height: "100%",
  backgroundColor: "white",
  position: "relative",
  overflow: "hidden",
  "&::before": { content: '""', position: "absolute", top: 0, left: 0, width: "4px", height: "100%", background: color },
  "&:hover": { transform: "translateY(-4px)", boxShadow: `0 12px 28px ${color}25`, borderColor: color },
}));

const PunchButton = styled(Button)(({ theme, punchvariant }) => ({
  padding: theme.spacing(2, 4),
  fontSize: "1.1rem",
  fontWeight: 700,
  borderRadius: theme.spacing(2),
  textTransform: "none",
  height: 64,
  ...(punchvariant === "in" && {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    "&:hover": { background: "linear-gradient(135deg, #059669 0%, #047857 100%)", boxShadow: "0 8px 24px rgba(16,185,129,0.4)" },
  }),
  ...(punchvariant === "out" && {
    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color: "white",
    "&:hover": { background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)", boxShadow: "0 8px 24px rgba(239,68,68,0.4)" },
  }),
}));

const StatusDot = styled(Box)(({ active }) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: active ? "#10b981" : "#94a3b8",
  animation: active ? `${pulse} 2s ease-in-out infinite` : "none",
  boxShadow: active ? "0 0 0 4px rgba(16, 185, 129, 0.2)" : "none",
}));

const RequestRow = styled(Box)(({ theme, status }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  borderLeft: `4px solid ${
    status === "pending" ? "#f59e0b"
    : ["approved", "fulfilled", "repaying", "closed"].includes(status) ? "#10b981"
    : status === "rejected" ? "#ef4444" : "#94a3b8"
  }`,
  backgroundColor: "#f8fafc",
  "&:hover": { backgroundColor: "#f1f5f9" },
}));

// ============== ✅ FIX #1: Normalize ANY employee response shape ==============
const normalizeEmployee = (data) => {
  if (!data) return {};
  if (data.employee && typeof data.employee === "object") return data.employee;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data.employee || data.data;
  }
  if (data.employee_name || data.name) return data;
  return {};
};

// ============== ✅ FIX #2: Normalize ANY punch-history shape ==============
const normalizeHistory = (data) => {
  const arr = Array.isArray(data) ? data
    : Array.isArray(data?.data) ? data.data
    : Array.isArray(data?.history) ? data.history
    : [];
  return arr.map((p) => ({
    ...p,
    type: (p.type || p.punch_type || (p.punch_in ? "in" : "out"))?.toLowerCase?.().replace("punch_", ""),
    timestamp: p.timestamp || p.punch_time || p.created_at,
  }));
};

// ============== Main Component ==============
const EmployeeDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState({});
  const [attendance, setAttendance] = useState({});
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [recentPayslips, setRecentPayslips] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestsTab, setRequestsTab] = useState("all");
  const [punching, setPunching] = useState(false);
  const [punchMethod, setPunchMethod] = useState("web");
  const [location, setLocation] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastPunch, setLastPunch] = useState(null);
  const [todayHistory, setTodayHistory] = useState([]);
  const [weekHistory, setWeekHistory] = useState([]);

  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type_id: "", start_date: "", end_date: "", duration_type: "full_day", reason: "" });
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    loadDashboardData();
    getLocation();
  }, []);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setLocation(null)
      );
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [profileRes, attendanceRes, leaveRes, payrollRes, leaveListRes, expenseRes, loanRes, reqRes, historyRes, weekRes] = await Promise.all([
        AxiosAuthInstance.get("hr/employee/me").catch(() => ({ data: {} })),
        AxiosAuthInstance.get("hr/attendance/summary").catch(() => ({ data: {} })),
        AxiosAuthInstance.get("hr/leave/balance").catch(() => ({ data: { balances: [] } })),
        AxiosAuthInstance.get("hr/payroll/recent").catch(() => ({ data: [] })),
        AxiosAuthInstance.get("hr/leave/my-leaves").catch(() => ({ data: [] })),
        AxiosAuthInstance.get("expenses").catch(() => ({ data: [] })),
        AxiosAuthInstance.get("hr/loans").catch(() => ({ data: [] })),
        AxiosAuthInstance.get("hr/requisitions/my").catch(() => ({ data: [] })),
        AxiosAuthInstance.get("hr/attendance/today-history").catch(() => ({ data: [] })),
        AxiosAuthInstance.get("hr/attendance/week-history").catch(() => ({ data: [] })),
      ]);

      // ✅ FIX #1: normalize shape + localStorage fallback for name
      let emp = normalizeEmployee(profileRes.data);
      if (!emp.employee_name) {
        const lsName =
          localStorage.getItem("employee_name") ||
          localStorage.getItem("name") ||
          localStorage.getItem("user_name") ||
          localStorage.getItem("username");
        if (lsName) emp = { ...emp, employee_name: lsName };
      }
      setEmployee(emp);

      setAttendance(attendanceRes.data || {});

      const balances = leaveRes.data?.balances || [];
      setLeaveBalance(balances);
      setLeaveTypes(balances.map((b) => ({ value: b.leave_type_id, label: b.name || b.leave_type?.name || "Leave" })));

      setRecentPayslips(Array.isArray(payrollRes.data) ? payrollRes.data.slice(0, 3) : payrollRes.data?.data?.slice?.(0, 3) || []);

      // ✅ FIX #3b: aggregate requests from the SAME 4 endpoints MyRequests.js uses
      const asArray = (d) => (Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []);
      const formatted = [
        ...asArray(leaveListRes.data).map((i) => ({
          id: i.id, type: "Leave", date: i.created_at,
          details: `${i.leave_type?.name || "Leave"} (${i.days_count} days)`,
          status: i.status, rejectionReason: i.rejected_reason || i.rejection_reason || null,
        })),
        ...asArray(expenseRes.data).map((i) => ({
          id: i.id, type: "Expense", date: i.created_at,
          details: `${i.category} - ৳${Number(i.amount || 0).toLocaleString()}`,
          status: i.status, rejectionReason: i.rejected_reason || i.rejection_reason || null,
        })),
        ...asArray(loanRes.data).map((i) => ({
          id: i.id, type: "Loan", date: i.created_at,
          details: `${i.type === "advance_salary" ? "Advance Salary" : "Loan"} - ৳${Number(i.amount || 0).toLocaleString()}`,
          status: i.status, rejectionReason: i.rejection_reason || null,
        })),
        ...asArray(reqRes.data).map((i) => ({
          id: i.id, type: "Requisition", date: i.created_at,
          details: `${i.item_name} (Qty: ${i.requested_quantity})`,
          status: i.status, rejectionReason: i.rejection_reason || null,
        })),
      ].sort((a, b) => new Date(b.date) - new Date(a.date));
      setRequests(formatted);

      // ✅ FIX #2: normalized history; pick LATEST punch by timestamp
      const history = normalizeHistory(historyRes.data);
      if (history.length > 0) {
        setTodayHistory(history);
        const latest = history.reduce((a, b) => (new Date(a.timestamp) > new Date(b.timestamp) ? a : b));
        setLastPunch(latest);
      }

      setWeekHistory(normalizeHistory.length ? (Array.isArray(weekRes.data) ? weekRes.data : weekRes.data?.data || []) : []);
    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIX #2: optimistic state update — UI reflects punch instantly
  const punch = async (type) => {
    const result = await Swal.fire({
      title: `Punch ${type.toUpperCase()}?`,
      text: `Record your ${type === "in" ? "start" : "end"} time now`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: type === "in" ? "#10b981" : "#ef4444",
      confirmButtonText: `Yes, Punch ${type}`,
    });
    if (!result.isConfirmed) return;

    setPunching(true);
    try {
      await AxiosAuthInstance.post("hr/attendance/punch", {
        type,
        method: punchMethod,
        latitude: location?.lat,
        longitude: location?.lng,
      });

      const newPunch = { type, method: punchMethod, timestamp: new Date().toISOString() };
      setLastPunch(newPunch);
      setTodayHistory((h) => [...h, newPunch]);

      Swal.fire({ icon: "success", title: `Punched ${type}!`, timer: 1500, showConfirmButton: false });
      loadDashboardData();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed", text: err.response?.data?.message || "Try again" });
    } finally {
      setPunching(false);
    }
  };

  const openLeaveModal = () => {
    setLeaveForm({ leave_type_id: "", start_date: "", end_date: "", duration_type: "full_day", reason: "" });
    setLeaveModalOpen(true);
  };

  const submitLeave = async () => {
    if (!leaveForm.leave_type_id || !leaveForm.start_date || !leaveForm.end_date || leaveForm.reason.length < 10) {
      Swal.fire({ icon: "warning", title: "Missing info", text: "Please fill all fields (reason min 10 chars)" });
      return;
    }
    const start = new Date(leaveForm.start_date);
    const end = new Date(leaveForm.end_date);
    const days = leaveForm.duration_type === "half_day" ? 0.5 : Math.ceil((end - start) / 86400000) + 1;

    setLeaveSubmitting(true);
    try {
      await AxiosAuthInstance.post("hr/leave/apply", { ...leaveForm, days_count: days });
      Swal.fire({ icon: "success", title: "Leave Applied!", timer: 1800, showConfirmButton: false });
      setLeaveModalOpen(false);
      loadDashboardData();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed", text: err.response?.data?.message || "Try again" });
    } finally {
      setLeaveSubmitting(false);
    }
  };

  // ---- Helpers ----
  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good Morning";
    if (h < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const getInitials = (name) => {
    if (!name) return "EM";
    const parts = name.split(" ");
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
  };

  const formatCurrency = (a) => `৳${Number(a || 0).toLocaleString("en-BD")}`;
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");
  const formatTime = (d) => (d ? new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }) : "—");

  // ✅ FIX #3a: late still counts as attended
  const attendanceData = useMemo(() => {
    const p = attendance.present || 0, l = attendance.late || 0, a = attendance.absent || 0;
    const total = p + l + a;
    return {
      rate: total > 0 ? Math.round(((p + l) / total) * 100) : 0,
      ring: [{ name: "Attendance", value: total > 0 ? Math.round(((p + l) / total) * 100) : 0, fill: "#10b981" }],
      breakdown: [
        { name: "Present", value: p, color: "#10b981" },
        { name: "Late", value: l, color: "#f59e0b" },
        { name: "Absent", value: a, color: "#ef4444" },
      ],
    };
  }, [attendance]);

  const leaveDonut = useMemo(
    () => leaveBalance.map((l) => ({ name: l.name || l.leave_type?.name || "Leave", value: l.remaining || 0, used: l.used || 0, allocated: l.allocated || 0 })),
    [leaveBalance]
  );
  const totalLeaveRemaining = useMemo(() => leaveBalance.reduce((s, l) => s + (l.remaining || 0), 0), [leaveBalance]);

  const weekChartData = useMemo(
    () => weekHistory.slice(-7).map((d) => ({ day: new Date(d.date).toLocaleDateString("en-US", { weekday: "short" }), hours: d.hours_worked || 0 })),
    [weekHistory]
  );

  const filteredRequests = useMemo(() => {
    if (requestsTab === "all") return requests.slice(0, 6);
    return requests.filter((r) => r.type?.toLowerCase() === requestsTab.toLowerCase()).slice(0, 6);
  }, [requests, requestsTab]);

  const pendingCount = useMemo(() => requests.filter((r) => r.status === "pending").length, [requests]);

  const quickActions = [
    { title: "Apply Leave", icon: FaCalendarAlt, color: "#3b82f6", action: openLeaveModal, description: "Quick leave request" },
    { title: "View Payslip", icon: FaFileInvoiceDollar, color: "#10b981", link: "/hr/payslips", description: "Download payslips" },
    { title: "My Documents", icon: FaIdCard, color: "#8b5cf6", link: "/hr/documents", description: "View your files" },
    { title: "Requisition", icon: FaBoxOpen, color: "#f59e0b", link: "/hr/requisition", description: "Request supplies" },
    { title: "Expenses", icon: FaMoneyBillWave, color: "#06b6d4", link: "/hr/expenses", description: "Submit claims" },
    { title: "AI Assistant", icon: FaRobot, color: "#ec4899", link: "/hr/ai-chat", description: "Get instant help" },
  ];

  const punchStatus = lastPunch?.type === "in" ? "in" : lastPunch?.type === "out" ? "out" : "none";
  const displayName = employee.employee_name || "Team Member";
  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4"];

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
        <Breadcrumb title="My Dashboard" />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, mb: 3 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => <Grid item xs={12} sm={6} md={3} key={i}><Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} /></Grid>)}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh", animation: `${fadeIn} 0.5s ease-out` }}>
      <Breadcrumb title="My Dashboard" />

      {/* ==================== HERO ==================== */}
      <HeroCard elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 3, position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flex: 1 }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 72, height: 72, fontSize: "1.75rem", fontWeight: 700, border: "3px solid rgba(255,255,255,0.3)" }}>
              {getInitials(employee.employee_name)}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {getGreeting()}, {displayName.split(" ")[0]}!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.95 }}>
                {employee.employee_designation || employee.designation || "Employee"}
                {(employee.department || employee.department_name) && ` • ${employee.department || employee.department_name}`}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                {currentTime.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ textAlign: "right", bgcolor: "rgba(255,255,255,0.15)", px: 2.5, py: 1.5, borderRadius: 2, backdropFilter: "blur(10px)" }}>
              <Typography variant="h4" fontWeight={700} sx={{ fontFamily: "monospace", lineHeight: 1 }}>
                {currentTime.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Your local time</Typography>
            </Box>
            <NotificationCenter
              requests={requests}
              leaveBalance={leaveBalance}
              payslips={recentPayslips}
              todayHistory={todayHistory}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={loadDashboardData} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
                <FaSync />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </HeroCard>

      {/* ==================== PUNCH HERO ==================== */}
      <PunchHero status={punchStatus} sx={{ mb: 3 }}>
        <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <StatusDot active={punchStatus === "in"} />
              <Typography variant="h5" fontWeight={700}>
                {punchStatus === "in" ? "You are Checked In" : punchStatus === "out" ? "You are Checked Out" : "Not Punched Yet"}
              </Typography>
            </Box>
            {lastPunch && (
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Last punch: <strong>{formatTime(lastPunch.timestamp)}</strong> ({lastPunch.type === "in" ? "Check In" : "Check Out"})
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                icon={punchMethod === "gps" ? <FaMapMarkerAlt /> : <FaGlobe />}
                label={punchMethod === "gps" ? "GPS Mode" : "Web Mode"}
                onClick={() => setPunchMethod(punchMethod === "web" ? "gps" : "web")}
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600, "& .MuiChip-icon": { color: "white" } }}
              />
              {location && punchMethod === "gps" && (
                <Chip label={`${location.lat.toFixed(3)}, ${location.lng.toFixed(3)}`} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }} />
              )}
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <PunchButton punchvariant="in" disabled={punching} onClick={() => punch("in")} startIcon={<FaFingerprint />}>
                {punching ? "Processing..." : "PUNCH IN"}
              </PunchButton>
              <PunchButton punchvariant="out" disabled={punching} onClick={() => punch("out")} startIcon={<FaSignOutAlt />}>
                {punching ? "Processing..." : "PUNCH OUT"}
              </PunchButton>
            </Stack>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, opacity: 0.9 }}>
              <FaClock style={{ marginRight: 6 }} />Today's Activity
            </Typography>
            <Box sx={{ bgcolor: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)", borderRadius: 2, p: 2, maxHeight: 180, overflowY: "auto" }}>
              {todayHistory.length > 0 ? (
                <Stack spacing={1}>
                  {todayHistory.slice().reverse().map((p, i) => (
                    <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: p.type === "in" ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {p.type === "in" ? <FaFingerprint size={14} /> : <FaSignOutAlt size={14} />}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{p.type === "in" ? "Check In" : "Check Out"}</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.7 }}>{p.method === "gps" ? "GPS" : "Web"}</Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>{formatTime(p.timestamp)}</Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 3, opacity: 0.7 }}>
                  <FaClock size={24} style={{ marginBottom: 8 }} />
                  <Typography variant="body2">No punches recorded today</Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </PunchHero>

      {/* ==================== METRICS GRID ==================== */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* ✅ FIX #3a: cleaner ring, no text overflow, late = attended */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <Typography variant="subtitle2" color="#64748b" fontWeight={600}>Attendance Rate</Typography>
              <Chip label={`${attendanceData.rate}%`} size="small" sx={{ bgcolor: "#ecfdf5", color: "#059669", fontWeight: 700 }} />
            </Box>
            <Box sx={{ position: "relative", height: 120, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="80%" outerRadius="100%" data={attendanceData.ring} startAngle={90} endAngle={-270}>
                  <RadialBar dataKey="value" cornerRadius={10} fill="#10b981" background={{ fill: "#f1f5f9" }} />
                </RadialBarChart>
              </ResponsiveContainer>
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="h5" fontWeight={800} color="#059669">{attendanceData.rate}%</Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="#64748b" sx={{ display: "block", textAlign: "center", mt: 0.5 }}>
              {attendance.present || 0} present • {attendance.late || 0} late • {attendance.absent || 0} absent
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: "space-around" }}>
              {attendanceData.breakdown.map((b) => (
                <Box key={b.name} sx={{ textAlign: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: b.color }} />
                    <Typography variant="caption" color="#64748b">{b.name}</Typography>
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>{b.value}</Typography>
                </Box>
              ))}
            </Stack>
          </MetricCard>
        </Grid>

        {/* Leave Donut */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <Typography variant="subtitle2" color="#64748b" fontWeight={600}>Leave Balance</Typography>
              <Chip label={`${totalLeaveRemaining} days`} size="small" sx={{ bgcolor: "#eff6ff", color: "#1e40af", fontWeight: 700 }} />
            </Box>
            <Box sx={{ height: 120, mt: 1 }}>
              {leaveDonut.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={leaveDonut} innerRadius={40} outerRadius={60} dataKey="value" paddingAngle={3}>
                      {leaveDonut.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <RechartsTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: "center", py: 5, color: "#94a3b8" }}>
                  <FaCalendarAlt size={28} style={{ marginBottom: 8 }} />
                  <Typography variant="caption">No leave data</Typography>
                </Box>
              )}
            </Box>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {leaveDonut.slice(0, 3).map((l, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS[i % COLORS.length] }} />
                    <Typography variant="caption" color="#475569">{l.name}</Typography>
                  </Box>
                  <Typography variant="caption" fontWeight={700} color="#1e293b">{l.remaining}/{l.allocated}</Typography>
                </Box>
              ))}
            </Stack>
          </MetricCard>
        </Grid>

        {/* Weekly Hours — honest empty state instead of fake bars */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <Typography variant="subtitle2" color="#64748b" fontWeight={600}>This Week</Typography>
              <Chip label={`${weekChartData.reduce((s, d) => s + d.hours, 0).toFixed(1)}h`} size="small" sx={{ bgcolor: "#faf5ff", color: "#7c3aed", fontWeight: 700 }} />
            </Box>
            <Box sx={{ height: 120, mt: 1 }}>
              {weekChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weekChartData}>
                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <RechartsTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Bar dataKey="hours" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: "center", py: 5, color: "#94a3b8" }}>
                  <FaChartLine size={28} style={{ marginBottom: 8 }} />
                  <Typography variant="caption">No hours recorded yet</Typography>
                </Box>
              )}
            </Box>
            <Typography variant="caption" color="#64748b" sx={{ display: "block", textAlign: "center", mt: 1 }}>
              Hours worked per day
            </Typography>
          </MetricCard>
        </Grid>

        {/* Pending Requests */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <Typography variant="subtitle2" color="#64748b" fontWeight={600}>Pending Requests</Typography>
              <Chip label="Action needed" size="small" sx={{ bgcolor: "#fff7ed", color: "#c2410c", fontWeight: 700 }} />
            </Box>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h1" fontWeight={800} color="#c2410c" sx={{ lineHeight: 1 }}>{pendingCount}</Typography>
              <Typography variant="body2" color="#64748b" sx={{ mt: 1 }}>request{pendingCount !== 1 ? "s" : ""} awaiting review</Typography>
            </Box>
            <Button fullWidth variant="outlined" endIcon={<FaArrowRight />} onClick={() => navigate("/hr/my-requests")}
              sx={{ mt: 1, textTransform: "none", fontWeight: 600, borderColor: "#fdba74", color: "#c2410c" }}>
              View All Requests
            </Button>
          </MetricCard>
        </Grid>
      </Grid>

      {/* ==================== LEAVE + REQUESTS ==================== */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #e2e8f0", height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#eff6ff", color: "#1e40af", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaCalendarCheck />
                </Box>
                <Typography variant="h6" fontWeight={700} color="#1e293b">Leave Balance</Typography>
              </Box>
              <Button size="small" variant="contained" startIcon={<FaPlus />} onClick={openLeaveModal}
                sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
                Apply
              </Button>
            </Box>
            <Stack spacing={1.5}>
              {leaveBalance.length > 0 ? leaveBalance.map((l, i) => {
                const pct = l.allocated > 0 ? (l.used / l.allocated) * 100 : 0;
                const color = COLORS[i % COLORS.length];
                return (
                  <Box key={i} sx={{ p: 2, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b">{l.name || l.leave_type?.name || "Leave"}</Typography>
                      </Box>
                      <Chip label={`${l.remaining} left`} size="small"
                        sx={{ bgcolor: pct > 80 ? "#fef2f2" : pct > 50 ? "#fef3c7" : "#ecfdf5", color: pct > 80 ? "#991b1b" : pct > 50 ? "#92400e" : "#065f46", fontWeight: 700, fontSize: 11 }} />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" color="#64748b">Used {l.used} of {l.allocated}</Typography>
                      <Typography variant="caption" fontWeight={700} color="#475569">{Math.round(pct)}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct}
                      sx={{ height: 6, borderRadius: 3, bgcolor: "#e2e8f0", "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 } }} />
                  </Box>
                );
              }) : (
                <Box sx={{ textAlign: "center", py: 4, color: "#94a3b8" }}>
                  <FaCalendarAlt size={32} style={{ marginBottom: 8 }} />
                  <Typography variant="body2">No leave policies assigned</Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #e2e8f0", height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#faf5ff", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaClipboardList />
                </Box>
                <Typography variant="h6" fontWeight={700} color="#1e293b">My Recent Requests</Typography>
              </Box>
              <Button size="small" endIcon={<FaArrowRight />} onClick={() => navigate("/hr/my-requests")} sx={{ textTransform: "none", fontWeight: 600 }}>
                View all
              </Button>
            </Box>

            <Tabs value={requestsTab} onChange={(_, v) => setRequestsTab(v)} variant="scrollable" scrollButtons="auto"
              sx={{ mb: 2, minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600, fontSize: 13 } }}>
              <Tab label="All" value="all" />
              <Tab label="Leave" value="leave" />
              <Tab label="Expense" value="expense" />
              <Tab label="Loan" value="loan" />
              <Tab label="Requisition" value="requisition" />
            </Tabs>

            <Stack spacing={1} sx={{ maxHeight: 320, overflowY: "auto" }}>
              {filteredRequests.length > 0 ? filteredRequests.map((r, i) => (
                <RequestRow key={i} status={r.status?.toLowerCase?.()}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {r.type === "Leave" ? <FaCalendarAlt color="#3b82f6" /> :
                         r.type === "Expense" ? <FaMoneyBillWave color="#10b981" /> :
                         r.type === "Loan" ? <FaHandHoldingUsd color="#f59e0b" /> :
                         <FaBoxOpen color="#8b5cf6" />}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b" noWrap>{r.details}</Typography>
                        <Typography variant="caption" color="#64748b">{r.type} • {formatDate(r.date)}</Typography>
                      </Box>
                    </Box>
                    <Chip
                      icon={r.status === "pending" ? <FaHourglassHalf style={{ fontSize: 10 }} /> : r.status === "rejected" ? <FaTimesCircle style={{ fontSize: 10 }} /> : <FaCheckCircle style={{ fontSize: 10 }} />}
                      label={r.status || "Unknown"} size="small"
                      sx={{
                        bgcolor: r.status === "pending" ? "#fef3c7" : r.status === "rejected" ? "#fee2e2" : "#dcfce7",
                        color: r.status === "pending" ? "#92400e" : r.status === "rejected" ? "#991b1b" : "#166534",
                        fontWeight: 700, textTransform: "capitalize", "& .MuiChip-icon": { color: "inherit" },
                      }}
                    />
                  </Box>
                  {r.status === "rejected" && r.rejectionReason && (
                    <Typography variant="caption" color="#991b1b" sx={{ mt: 0.5, display: "block", fontStyle: "italic" }}>
                      Reason: {r.rejectionReason}
                    </Typography>
                  )}
                </RequestRow>
              )) : (
                <Box sx={{ textAlign: "center", py: 5, color: "#94a3b8" }}>
                  <FaClipboardList size={32} style={{ marginBottom: 8 }} />
                  <Typography variant="body2">No requests in this category</Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* ==================== QUICK ACTIONS ==================== */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Typography variant="h6" fontWeight={700} color="#1e293b" sx={{ mb: 2.5 }}>
          <FaClipboardList style={{ marginRight: 8, color: "#6366f1" }} />
          Self-Service Quick Actions
        </Typography>
        <Grid container spacing={2}>
          {quickActions.map((a, i) => {
            const Icon = a.icon;
            return (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <QuickActionCard color={a.color} onClick={() => (a.action ? a.action() : navigate(a.link))}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 1.5, bgcolor: `${a.color}15`, color: a.color, display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
                    <Icon size={22} />
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#1e293b" gutterBottom>{a.title}</Typography>
                  <Typography variant="caption" color="#64748b">{a.description}</Typography>
                </QuickActionCard>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* ==================== PAYSLIPS ==================== */}
      {recentPayslips.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: "1px solid #e2e8f0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#ecfdf5", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FaFileInvoiceDollar />
              </Box>
              <Typography variant="h6" fontWeight={700} color="#1e293b">Recent Payslips</Typography>
            </Box>
            <Button size="small" endIcon={<FaArrowRight />} onClick={() => navigate("/hr/payslips")} sx={{ textTransform: "none", fontWeight: 600 }}>
              View all
            </Button>
          </Box>
          <Grid container spacing={2}>
            {recentPayslips.map((p, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="caption" color="#64748b">{p.period?.period_name || "Period"}</Typography>
                    <Typography variant="h5" fontWeight={800} color="#059669" sx={{ my: 0.5 }}>{formatCurrency(p.net_salary)}</Typography>
                    <Chip label={p.status || "Pending"} size="small" sx={{ bgcolor: "#f1f5f9", fontWeight: 600 }} />
                  </Box>
                  <Tooltip title="Download">
                    <IconButton onClick={() => window.open(`/api/v1/hr/payroll/${p.id}/download?token=${localStorage.getItem("token")}`, "_blank")}
                      sx={{ bgcolor: "#ecfdf5", color: "#059669", "&:hover": { bgcolor: "#d1fae5" } }}>
                      <FaDownload />
                    </IconButton>
                  </Tooltip>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* ==================== QUICK LEAVE MODAL ==================== */}
      <Dialog open={leaveModalOpen} onClose={() => setLeaveModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#6366f1", color: "white", py: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <FaCalendarAlt />
            <Typography variant="h6" fontWeight={700}>Quick Leave Application</Typography>
            <IconButton onClick={() => setLeaveModalOpen(false)} sx={{ ml: "auto", color: "white" }}><FaTimes /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <TextField select fullWidth label="Leave Type *" value={leaveForm.leave_type_id}
              onChange={(e) => setLeaveForm({ ...leaveForm, leave_type_id: e.target.value })}>
              {leaveTypes.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Radio checked={leaveForm.duration_type === "full_day"} onChange={() => setLeaveForm({ ...leaveForm, duration_type: "full_day" })} sx={{ color: "#6366f1", "&.Mui-checked": { color: "#6366f1" } }} />} label="Full Day" />
              <FormControlLabel control={<Radio checked={leaveForm.duration_type === "half_day"} onChange={() => setLeaveForm({ ...leaveForm, duration_type: "half_day", end_date: leaveForm.start_date })} sx={{ color: "#6366f1", "&.Mui-checked": { color: "#6366f1" } }} />} label="Half Day" />
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField type="date" fullWidth label="Start Date *" value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }} inputProps={{ min: new Date().toISOString().split("T")[0] }} />
              </Grid>
              <Grid item xs={6}>
                <TextField type="date" fullWidth label="End Date *" value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }} inputProps={{ min: leaveForm.start_date || new Date().toISOString().split("T")[0] }} />
              </Grid>
            </Grid>
            <TextField multiline rows={3} fullWidth label="Reason *" value={leaveForm.reason}
              onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
              placeholder="Minimum 10 characters..." helperText={`${leaveForm.reason.length}/10 characters minimum`} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setLeaveModalOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={submitLeave} disabled={leaveSubmitting}
            startIcon={leaveSubmitting ? <CircularProgress size={16} color="inherit" /> : <FaPaperPlane />}
            sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
            {leaveSubmitting ? "Submitting..." : "Submit Leave"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeDashboard;